/**
 * resør · Family strip (spec & model grouping add-on)
 * ---------------------------------------------------------------------------
 * Custom element <resor-family-strip>, rendered by snippets/resor-family-strip.liquid.
 *
 * What it does
 *   1. Keeps the customer's choice when moving between models: while
 *      "In stock" is selected, links to models that have stock carry
 *      ?build=stock so the next model opens on In stock too.
 *      Listens for `resor:mode-change` (dispatched by resor-order-switch.js).
 *   2. Makes the next page load fast: the sibling page is prefetched on
 *      hover / touch / focus (and, for the siblings, once the browser is
 *      idle), so the click is served from the browser cache.
 *   3. Centres the current model and shows prev / next arrows when the row
 *      overflows.
 *
 * Navigation is a normal page load (the cards are plain links)
 *   Until 25 Sep 2026 a click swapped the product section in place. That was
 *   removed because EasyFlow Product Options cannot follow a swap: on page
 *   load it records which product's variants it handles (the option block's
 *   data-variant-ids) and formats option prices once. After a swap it ignored
 *   the new mixer's add to cart, so the chosen spec and the add-on lines
 *   (e.g. wood, travel case) never reached the cart. EasyFlow keeps that
 *   list private, so it cannot be updated from outside. A full page load
 *   starts EasyFlow, Downpay and the theme fresh, which is always correct.
 *
 *   The page change still reads as one step: resor-order-switch.css turns on
 *   cross-document view transitions (@view-transition), so the strip and the
 *   switch stay in place while the rest cross-fades (Chrome, Edge, Safari 18.2+;
 *   other browsers just load the page). The links carry Motion's
 *   js-no-transition class so Motion's own fade-out does not delay the click.
 *
 * No dependencies. Safe to load more than once.
 */
(() => {
  if (customElements.get('resor-family-strip')) return;

  /** URLs already prefetched on this page (one <link rel="prefetch"> each). */
  const prefetched = new Set();

  class ResorFamilyStrip extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.links = [...this.querySelectorAll('[data-resor-family-link]')];

      this.onModeChange = (event) => this.updateLinks(event.detail && event.detail.mode);
      document.addEventListener('resor:mode-change', this.onModeChange);

      this.links.forEach((link) => {
        // Motion's "page transitions" binds a fade-then-navigate click handler
        // to every link without this class. Liquid sets it too; this covers a
        // strip rendered any other way.
        link.classList.add('js-no-transition');
        link.addEventListener('mouseenter', () => this.prefetch(link), { passive: true });
        link.addEventListener('touchstart', () => this.prefetch(link), { passive: true });
        link.addEventListener('focus', () => this.prefetch(link));
      });

      // Pick up a mode that was set before this element initialised (e.g. ?build=stock).
      const switchEl = document.querySelector('resor-order-switch');
      this.updateLinks(switchEl && switchEl.dataset.mode);

      this.setupArrows();

      const current = this.querySelector('.resor-family__link.is-current');
      if (current && this.list) {
        this.list.scrollLeft = current.offsetLeft - (this.list.clientWidth - current.offsetWidth) / 2;
      }

      // Warm the cache for the siblings once the page is idle (skipped on Save-Data).
      const conn = navigator.connection;
      if (!(conn && conn.saveData)) {
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
        idle(() => this.links.forEach((link) => this.prefetch(link)));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('resor:mode-change', this.onModeChange);
    }

    /**
     * Asks the browser to fetch the sibling page ahead of the click. Uses the
     * link's current href, so a prefetch made after switching to In stock
     * already includes ?build=stock.
     * @param {HTMLAnchorElement} link
     */
    prefetch(link) {
      if (link.classList.contains('is-current')) return;
      const href = link.href;
      if (prefetched.has(href)) return;
      prefetched.add(href);
      const hint = document.createElement('link');
      hint.rel = 'prefetch';
      hint.as = 'document';
      hint.href = href;
      document.head.appendChild(hint);
    }

    /* ------------------------------------------------------------------ */
    /* Links & arrows                                                     */
    /* ------------------------------------------------------------------ */

    /**
     * Adds ?build=stock to sibling links that have stock while In stock is active.
     * @param {'bespoke'|'stock'|undefined} mode
     */
    updateLinks(mode) {
      this.links.forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        const hasStock = parseInt(link.dataset.inStock, 10) > 0;

        if (mode === 'stock' && hasStock) {
          url.searchParams.set('build', 'stock');
        } else {
          url.searchParams.delete('build');
        }
        url.searchParams.delete('unit');
        link.href = url.pathname + url.search;
      });
    }

    /** Prev / next buttons: shown only when the row overflows. */
    setupArrows() {
      this.list = this.querySelector('[data-resor-family-list]');
      this.prevBtn = this.querySelector('[data-resor-family-prev]');
      this.nextBtn = this.querySelector('[data-resor-family-next]');
      if (!this.list || !this.prevBtn || !this.nextBtn) return;

      const step = () => Math.max(this.list.clientWidth * 0.8, 120);
      this.prevBtn.addEventListener('click', () => this.list.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.nextBtn.addEventListener('click', () => this.list.scrollBy({ left: step(), behavior: 'smooth' }));

      const update = () => {
        const max = this.list.scrollWidth - this.list.clientWidth;
        const overflows = max > 2;
        this.prevBtn.hidden = !overflows;
        this.nextBtn.hidden = !overflows;
        this.prevBtn.disabled = this.list.scrollLeft <= 2;
        this.nextBtn.disabled = this.list.scrollLeft >= max - 2;
      };
      this.list.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    }
  }

  customElements.define('resor-family-strip', ResorFamilyStrip);
})();